import { useEffect, useRef, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  createFeatureRequest,
  findSimilarFeatureRequests,
  joinFeatureRequest,
  type SimilarFeatureRequest,
  useSupportTicketsEnabled,
} from '@/lib/api/support.js';

import {
  DEFAULT_FEEDBACK_URL,
  getRuntimeConfig,
} from '@/lib/runtime-config.js';
import { safeHttpUrl } from '@/lib/safe-url.js';
import { safePageUrl } from '@/features/bug-report/bug-report.js';

/**
 * Home-page banner pointing at the channel where users ask for features.
 *
 * Permanently `hidden`: the global {@link FeatureRequestDialog} reachable from
 * `FeedbackLauncher` on every route now covers this same "Request a feature" flow, so
 * showing this banner too would duplicate it on the one route it used to be exclusive to.
 * `FeatureRequestDialog` and its supporting hooks stay exported from this file and in use.
 *
 * The destination is runtime config (GET /v1/config → `feedbackUrl`), not a build-time
 * constant, so an admin can repoint it without shipping a new bundle. The value is
 * operator-supplied, so it goes through `safeHttpUrl`; anything that is not http(s)
 * falls back to the built-in default, as does a server that omits the field.
 *
 * It is one sentence, so it is laid out as one: plain inline text the browser breaks at word
 * boundaries. It used to be a flex row of four children spaced by `gap-2`, and `flex` never
 * breaks between its items — under about 390px each fragment wrapped inside its own box and the
 * sentence came out as a ragged block (MEDA-505). Keep the spaces as text; a `gap` is not a space.
 */
export function FeatureRequestBanner(): JSX.Element {
  const canFileTickets = useSupportTicketsEnabled();
  const [open, setOpen] = useState(false);
  const feedbackUrl =
    safeHttpUrl(getRuntimeConfig().feedbackUrl) || DEFAULT_FEEDBACK_URL;

  return (
    <div className="hidden text-balance text-center text-sm text-muted-foreground">
      Missing a feature? Tell us{' '}
      {canFileTickets ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-primary underline underline-offset-2"
        >
          here
        </button>
      ) : (
        <a
          href={feedbackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          here
        </a>
      )}{' '}
      and we'll build it <strong className="text-foreground">fast</strong>
      <FeatureRequestDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

export interface FeatureRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The reusable feature-request flow shared by the Home banner and global feedback launcher. */
export function FeatureRequestDialog({
  open,
  onOpenChange,
}: FeatureRequestDialogProps): JSX.Element | null {
  if (!open) return null;
  return <FeatureRequestDialogSession onOpenChange={onOpenChange} />;
}

function FeatureRequestDialogSession({
  onOpenChange,
}: Pick<FeatureRequestDialogProps, 'onOpenChange'>): JSX.Element {
  const [description, setDescription] = useState('');
  const [matches, setMatches] = useState<SimilarFeatureRequest[]>([]);
  const [stage, setStage] = useState<
    'write' | 'matches' | 'search-failed' | 'filed' | 'joined'
  >('write');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const generation = useRef(0);

  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  function changeOpen(next: boolean): void {
    if (!next) generation.current += 1;
    onOpenChange(next);
  }

  async function create(
    sent = generation.current,
    text = description.trim(),
  ): Promise<void> {
    if (sent !== generation.current) return;
    setBusy(true);
    setFailure(null);
    try {
      await createFeatureRequest({
        description: text,
        context: { pageUrl: safePageUrl(window.location) },
      });
      if (sent === generation.current) setStage('filed');
    } catch (error) {
      if (sent === generation.current) {
        setFailure(
          error instanceof Error
            ? error.message
            : 'Could not reach the server.',
        );
      }
    } finally {
      if (sent === generation.current) setBusy(false);
    }
  }

  async function continueToMatches(): Promise<void> {
    const sent = generation.current;
    const text = description.trim();
    setBusy(true);
    setFailure(null);
    try {
      const found = await findSimilarFeatureRequests(text);
      if (sent !== generation.current) return;
      if (found.length === 0) {
        await create(sent, text);
        return;
      }
      setMatches(found);
      setStage('matches');
    } catch {
      if (sent === generation.current) setStage('search-failed');
    } finally {
      if (sent === generation.current) setBusy(false);
    }
  }

  async function join(issueKey: string): Promise<void> {
    const sent = generation.current;
    setBusy(true);
    setFailure(null);
    try {
      await joinFeatureRequest(issueKey);
      if (sent === generation.current) setStage('joined');
    } catch (error) {
      if (sent === generation.current) {
        setFailure(
          error instanceof Error
            ? error.message
            : 'Could not reach the server.',
        );
      }
    } finally {
      if (sent === generation.current) setBusy(false);
    }
  }

  return (
    <AppDialog
      open
      onOpenChange={changeOpen}
      title="Request a feature"
      description={
        stage === 'matches'
          ? 'We found requests that may already cover this idea.'
          : stage === 'filed' || stage === 'joined'
            ? undefined
            : 'Tell us what you want to do and why it would be useful.'
      }
      className="sm:max-w-lg"
    >
      <div className="flex flex-col gap-3 text-left">
        {(stage === 'write' || stage === 'search-failed') && (
          <>
            <label className="flex flex-col gap-1.5 text-mini font-semibold uppercase tracking-wider text-muted-foreground">
              What would you like to do?
              <textarea
                value={description}
                rows={5}
                maxLength={5000}
                disabled={busy}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Describe what you want to do and why it would be useful"
                className="w-full resize-none rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground/30 focus:bg-card focus:ring-2 focus:ring-foreground/10"
              />
            </label>
            {stage === 'search-failed' && (
              <p role="alert" className="text-sm text-muted-foreground">
                We could not check for similar requests. You can still create a
                new one.
              </p>
            )}
            <Button
              size="sm"
              className="self-start"
              disabled={busy || description.trim() === ''}
              onClick={() =>
                void (stage === 'search-failed'
                  ? create()
                  : continueToMatches())
              }
            >
              {busy
                ? 'Checking…'
                : stage === 'search-failed'
                  ? 'Create request'
                  : 'Continue'}
            </Button>
          </>
        )}

        {stage === 'matches' && (
          <>
            <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
              {matches.map((match) => (
                <div
                  key={match.issueKey}
                  className="rounded-lg border border-border p-3"
                >
                  <p className="text-sm font-medium text-foreground">
                    {match.summary}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {match.status}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    disabled={busy}
                    onClick={() => void join(match.issueKey)}
                  >
                    Join existing
                  </Button>
                </div>
              ))}
            </div>
            <Button
              size="sm"
              className="self-start"
              disabled={busy}
              onClick={() => void create()}
            >
              {busy ? 'Sending…' : 'Create new anyway'}
            </Button>
          </>
        )}

        {stage === 'filed' && (
          <p role="status" className="text-sm text-foreground">
            Thanks, we passed this to the team.
          </p>
        )}
        {stage === 'joined' && (
          <p role="status" className="text-sm text-foreground">
            Thanks, you joined this request.
          </p>
        )}
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </AppDialog>
  );
}
